import { userForGrid } from '../../../spd/components/Users/Users';
import { GridRecord } from '../../components/Grid/GridDataTypes';
import {
  CreateUpdateUserDto,
  GetUserDto,
  CreateUpdateUserDtoFromJSON,
  GetLastEditorDto,
  UploadCertificateUserDto,
} from '../autogenerated/spdApiClient';
import { DefaultApi } from '../autogenerated/spdApiClient/apis/DefaultApi';
import { UserSchema } from '../validation/schemas';
import { validateAgainst } from '../validation/validateAgainst';

export type User = GetUserDto;

export type PublicUser = {
  id: string;
};

export interface UserContextInterface {
  getById: (id: string | null | undefined) => Promise<User | undefined>;
  getMe: () => Promise<User | undefined>;
  getByUserUID: (userUID: string | undefined) => Promise<User | undefined>;
  getAllByRoles: (roles: string[]) => Promise<GetUserDto[]>;
  uploadCertificate: (
    id: string,
    dto: UploadCertificateUserDto,
  ) => Promise<void>;
  getAllByPortion: (
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    mode?: string,
    filterColumns?: string,
  ) => Promise<GridRecord[]>;
  getByEmail: (email: string | null) => Promise<PublicUser | undefined>;
  getAllCount: (
    filterText?: string,
    mode?: string,
    filterColumns?: string,
  ) => Promise<number>;
  update: (id: string, dto: CreateUpdateUserDto) => Promise<void>;
  updateGet: (id: string) => Promise<void>;
  create: (dto: CreateUpdateUserDto) => Promise<GetUserDto>;
  updateEmail: (
    id: string | null | undefined,
    email: string,
  ) => Promise<void | undefined>;
  savePin: (id: string | null | undefined, pin: string) => Promise<void>;
  setSignP1WithSzafir: (id: string, signP1WithSzafir: boolean) => Promise<void>;
  getLastEditor: (id: string) => Promise<GetLastEditorDto>;
  syncFirebaseClaims: () => Promise<void>;
}

export const UserContext = (api: DefaultApi) => {
  return {
    getById: async (id: string | null | undefined) => {
      if (!id || id === undefined) {
        return;
      }
      return await api.userControllerGet(id);
    },

    getMe: async () => {
      const user = await api.userControllerGetMe();
      return user as unknown as User | undefined;
    },

    getByUserUID: async (userUID: string | undefined) => {
      if (!userUID) {
        return;
      }

      const profs = await api.userControllerGetByUid(userUID);
      if (profs.length === 1) {
        return profs[0] as unknown as Promise<User>;
      }
    },

    getByEmail: async (email: string | null) => {
      if (!email) {
        return;
      }
      const users = await api.userControllerGetByEmail(email);
      if (users.length === 1) {
        return { id: users[0].id } as PublicUser;
      } else if (users.length > 0) {
        throw new Error(
          'Znaleziono więcej niż jednego użytkownika o podanym adresie email',
        );
      } else {
        throw new Error('Nie znaleziono użytkownika o podanym adresie email');
      }
    },

    getAllByPortion: async (
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      mode?: string,
      filterColumns?: string,
    ) => {
      const data = await api.userControllerGetAllByPortion(
        pageSize,
        pageNumber,
        orderBy || '',
        orderDirection || '',
        filterText || '',
        mode,
        filterColumns,
      );
      return data.map(userForGrid);
    },

    getAllCount: (filterText?: string, mode?: string, filterColumns?: string) =>
      api.userControllerGetAllCount(filterText || '', mode, filterColumns),

    getAllByRoles: (roles: string[]) => api.userControllerGetAllByRoles(roles),

    update: (id: string, dto: CreateUpdateUserDto) =>
      api.userControllerUpdate(id, dto),

    updateGet: async (id: string) => {
      if (!id) {
        return;
      }

      return await api.userControllerRegister(id);
    },

    updateEmail: async (id: string | null | undefined, email: string) => {
      if (!id || id === undefined) {
        return;
      }
      return await api.userControllerChangeEmail(id, { email: email });
    },

    create: (dto: CreateUpdateUserDto) => api.userControllerCreate(dto),

    savePin: async (id: string | null | undefined, pin: string) => {
      if (!id || id === undefined) {
        return;
      }
      return await api.userControllerChangePin(id, { pin: pin });
    },

    setSignP1WithSzafir: async (id: string, signP1WithSzafir: boolean) => {
      return await api.userControllerChangeSignP1WithSzafir(id, {
        signP1WithSzafir,
      });
    },

    getLastEditor: (id: string) => api.userControllerGetLastEditor(id),

    uploadCertificate: (id: string, dto: UploadCertificateUserDto) =>
      api.userControllerUploadCertificate(id, dto),

    syncFirebaseClaims: () => api.userControllerSyncFirebaseClaims(),
  };
};

export const userData = (user?: GetUserDto): CreateUpdateUserDto => {
  return CreateUpdateUserDtoFromJSON({
    ...user,
    companyIds: user?.companiesInUser
      ?.filter(o => o !== null && o !== undefined)
      ?.map(c => c.company?.id)
      ?.filter(Boolean),
    healthcareFacilities: user?.usersInHealthcareFacility
      ?.filter(o => o !== null && o !== undefined)
      ?.map(hf => hf.healthcareFacility),
  });
};

export const validate = (user: CreateUpdateUserDto) =>
  validateAgainst(UserSchema, user);
